import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import { History, ChevronDown, ChevronUp } from 'lucide-react';

const ModificationHistorySection = ({ order, hidePricing = false }) => {
  const { t } = useTranslation('wholesaler');
  const [isExpanded, setIsExpanded] = useState(false);

  if (!order.modificationHistory || order.modificationHistory.length === 0) {
    return null;
  }

  // Helper: Calculate the effective total after applying discounts
  const getEffectiveTotal = (rawTotal) => {
    if (!rawTotal) return 0;
    let total = rawTotal;

    // Apply catalog discount if exists
    if (order.catalogDiscount?.percentage > 0) {
      total = total * (1 - order.catalogDiscount.percentage / 100);
    }

    // Apply customer discount if exists
    if (order.discount?.type !== 'none' && order.discount?.percentage > 0) {
      total = total * (1 - order.discount.percentage / 100);
    }

    return Math.round(total * 100) / 100;
  };

  // Helper: Check if order has any discounts
  const hasDiscounts = () => {
    return (order.catalogDiscount?.percentage > 0) || 
           (order.discount?.type !== 'none' && order.discount?.percentage > 0);
  };

  return (
    <div className="mb-6 bg-orange-50 border border-orange-200 rounded-lg p-4">
      <div
        onClick={() => setIsExpanded(!isExpanded)}
        className="flex items-center justify-between cursor-pointer"
      >
        <div className="flex items-center gap-2">
          <History className="w-5 h-5 text-orange-600" />
          <h4 className="font-semibold text-orange-900">
            {t('orders.modHistory.title', 'Ιστορικό Τροποποιήσεων Παραγγελίας')} ({order.modificationHistory.length})
          </h4>
        </div>
        {isExpanded ? (
          <ChevronUp className="w-5 h-5 text-orange-600" />
        ) : (
          <ChevronDown className="w-5 h-5 text-orange-600" />
        )}
      </div>

      {isExpanded && (
        <div className="mt-4 space-y-4">
          {order.modificationHistory.map((mod, idx) => {
            // Calculate discount-aware totals
            const prevRawTotal = mod.previousTotals?.subtotal || mod.previousTotals?.total || 0;
            const newRawTotal = mod.newTotals?.subtotal || mod.newTotals?.total || 0;
            const prevEffective = getEffectiveTotal(prevRawTotal);
            const newEffective = getEffectiveTotal(newRawTotal);

            return (
              <div key={idx} className="bg-white rounded-lg p-4 border border-orange-200">
                <div className="flex justify-between items-start mb-3">
                  <div>
                    <p className="font-medium text-gray-900">
                      {t('orders.modHistory.modifiedBy', 'Τροποποιήθηκε από')} {mod.modifierName}
                    </p>
                    <p className="text-sm text-gray-600">
                      {format(new Date(mod.modifiedAt), 'dd/MM/yy HH:mm')}
                    </p>
                    {mod.reason && (
                      <p className="text-sm text-gray-700 mt-1">
                        <span className="font-medium">{t('orders.modHistory.reason', 'Αιτία')}:</span> {mod.reason}
                      </p>
                    )}
                  </div>
                  <span className={`px-2 py-1 rounded text-xs font-medium ${
                    mod.modifiedBy === 'business' 
                      ? 'bg-blue-100 text-blue-800' 
                      : 'bg-orange-100 text-orange-800'
                  }`}>
                    {mod.modifiedBy === 'business' 
                      ? t('orders.modHistory.business', 'Επιχείρηση') 
                      : t('orders.modHistory.wholesaler', 'Χονδρέμπορος')}
                  </span>
                </div>

                {/* Changes Summary */}
                {mod.changes && mod.changes.length > 0 && (
                  <div className="space-y-2">
                    <p className="text-sm font-medium text-gray-700">{t('orders.modHistory.changesMade', 'Αλλαγές')}:</p>
                    {mod.changes.map((change, changeIdx) => (
                      <div key={changeIdx} className="text-sm bg-gray-50 p-2 rounded">
                        <span className="font-medium">{change.itemName}:</span>{' '}
                        <span className="capitalize">{change.field}</span>{' '}
                        <span className="text-red-600 line-through">
                          {hidePricing && change.field.includes('Price') ? '—'
                            : typeof change.oldValue === 'number' && change.field.includes('Price')
                              ? `€${change.oldValue.toFixed(2)}`
                              : change.oldValue}
                        </span>
                        {' → '}
                        <span className="text-green-600 font-medium">
                          {hidePricing && change.field.includes('Price') ? '—'
                            : typeof change.newValue === 'number' && change.field.includes('Price')
                              ? `€${change.newValue.toFixed(2)}`
                              : change.newValue}
                        </span>
                      </div>
                    ))}
                  </div>
                )}

                {/* Previous vs New Items Comparison */}
                <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Previous Items */}
                  <div className="bg-red-50 border border-red-200 rounded p-3">
                    <p className="text-sm font-semibold text-red-900 mb-2">{t('orders.modHistory.previousOrder', 'Προηγούμενη Παραγγελία')}:</p>
                    <div className="space-y-1 text-xs">
                      {mod.previousItems?.map((item, itemIdx) => (
                        <div key={itemIdx} className="flex justify-between">
                          <span className="text-gray-700">{item.name}</span>
                          <span className="font-medium">
                            {hidePricing ? `${item.quantity} ×` : `${item.quantity} × €${item.unitPrice?.toFixed(2)}`}
                          </span>
                        </div>
                      ))}
                    </div>
                    {!hidePricing && (
                    <div className="mt-2 pt-2 border-t border-red-300 flex justify-between text-sm font-bold">
                      <span>{t('orders.totals.subtotal', 'Υποσύνολο')}:</span>
                      <span className="text-red-700">€{prevRawTotal.toFixed(2)}</span>
                    </div>
                    )}
                    {!hidePricing && hasDiscounts() && (
                      <div className="flex justify-between text-xs text-red-600 mt-1">
                        <span>{t('orders.modHistory.discountNote', 'Μετά εκπτώσεις')}:</span>
                        <span className="font-semibold">€{prevEffective.toFixed(2)}</span>
                      </div>
                    )}
                  </div>

                  {/* New Items */}
                  <div className="bg-green-50 border border-green-200 rounded p-3">
                    <p className="text-sm font-semibold text-green-900 mb-2">{t('orders.modHistory.updatedOrder', 'Ενημερωμένη Παραγγελία')}:</p>
                    <div className="space-y-1 text-xs">
                      {mod.newItems?.filter(item => item.status !== 'removed').map((item, itemIdx) => (
                        <div key={itemIdx} className="flex justify-between">
                          <span className="text-gray-700">
                            {item.name}
                            {item.status === 'out_of_stock' && ` (${t('orders.itemStatus.outOfStock', 'Εξαντλημένο')})`}
                            {item.status === 'modified' && ` (${t('orders.itemStatus.modified', 'Τροποποιημένο')})`}
                          </span>
                          <span className="font-medium">
                            {hidePricing ? `${item.quantity} ×` : `${item.quantity} × €${item.unitPrice?.toFixed(2)}`}
                          </span>
                        </div>
                      ))}
                    </div>
                    {!hidePricing && (
                    <div className="mt-2 pt-2 border-t border-green-300 flex justify-between text-sm font-bold">
                      <span>{t('orders.totals.subtotal', 'Υποσύνολο')}:</span>
                      <span className="text-green-700">€{newRawTotal.toFixed(2)}</span>
                    </div>
                    )}
                    {!hidePricing && hasDiscounts() && (
                      <div className="flex justify-between text-xs text-green-600 mt-1">
                        <span>{t('orders.modHistory.discountNote', 'Μετά εκπτώσεις')}:</span>
                        <span className="font-semibold">€{newEffective.toFixed(2)}</span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Removed Items */}
                {mod.newItems?.filter(item => item.status === 'removed').length > 0 && (
                  <div className="mt-3 bg-gray-100 border border-gray-300 rounded p-3">
                    <p className="text-sm font-semibold text-gray-900 mb-2">{t('orders.modHistory.removedItems', 'Αφαιρεμένα Προϊόντα')}:</p>
                    <div className="space-y-1 text-xs">
                      {mod.newItems.filter(item => item.status === 'removed').map((item, itemIdx) => (
                        <div key={itemIdx} className="flex justify-between text-gray-600 line-through">
                          <span>{item.name}</span>
                          <span>{hidePricing ? `${item.quantity} ×` : `${item.quantity} × €${item.unitPrice?.toFixed(2)}`}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Price Difference - now discount-aware */}
                {!hidePricing && prevRawTotal !== newRawTotal && (
                  <div className="mt-3 pt-3 border-t border-gray-200">
                    <div className="flex justify-between items-center">
                      <span className="text-sm font-medium text-gray-700">{t('orders.modHistory.priceDifference', 'Διαφορά Τιμής')}:</span>
                      <div className="text-right">
                        {hasDiscounts() ? (
                          <>
                            <span className={`text-lg font-bold ${
                              newEffective > prevEffective ? 'text-red-600' : 'text-green-600'
                            }`}>
                              {newEffective > prevEffective ? '+' : ''}€{(newEffective - prevEffective).toFixed(2)}
                            </span>
                            <p className="text-xs text-gray-500">
                              ({t('orders.modHistory.discountNote', 'Τα σύνολα περιλαμβάνουν τις εφαρμοσμένες εκπτώσεις')})
                            </p>
                          </>
                        ) : (
                          <span className={`text-lg font-bold ${
                            newRawTotal > prevRawTotal ? 'text-red-600' : 'text-green-600'
                          }`}>
                            {newRawTotal > prevRawTotal ? '+' : ''}€{(newRawTotal - prevRawTotal).toFixed(2)}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default ModificationHistorySection;